import React, { useCallback, useContext, useEffect, useState } from 'react';

import '../styles/Homepage.css';
import HeroSlideshow from './HeroSlideshow';
import FeatureList from './FeatureList';
import CollectionList from './CollectionList';
import ProductList from './ProductList';
import { Link } from 'react-router-dom';
import ArticleList from './ArticleList';
import BiChevronRight from '../icons/BiChevronRight';
import { bool } from 'prop-types';
import SEO from './SEO';
import AppContext from '../components/context/AppContext';
import { useMemo } from 'react';
import useFeatures from '../hooks/useFeatures';
import SiteReviews from './SiteReviews';
import { OverlayTrigger } from 'react-bootstrap';
import QuoteForm from './QuoteForm';
import constants from '../constants.json' assert { type: 'json' };
import useDynamicPath from '../hooks/useDynamicPath';

const COLLECTION_LIST_LIMIT = 4;
const PRODUCT_LIST_LIMIT = 14;
const ARTICLE_LIST_LIMIT = 3;

// Homepage component
// Rendered at the root path
const Homepage = ({ isMobile }) => {
    const { appContext } = useContext(AppContext);
    const articlePath = useDynamicPath({ pathName: constants.DYNAMIC_PATHS.ARTICLE_PATH });
    const appConfig = appContext.config.themeConfig;
    const slideShowItems = useMemo(() => [...appConfig.heroSection.map((section) => ({
        backgroundUrl: section.backgroundUrl,
        headingText: section.headingText,
        paragraphText: section.paragraphText,
        buttonText: section.buttonText,
        buttonUrl: section.buttonUrl,
    }))], [appConfig]);
    const featureList = useFeatures();
    const [collectionList, setCollectionList] = useState([]);
    const [productList, setProductList] = useState([]);
    const [bestSellersList, setBestSellersList] = useState([]);
    const [articleList, setArticleList] = useState([]);
    const [showQuoteForm, setShowQuoteForm] = useState(false);
    const fetchCollectionList = useCallback(async () => {
        try {
            const params = new URLSearchParams({
                limit: COLLECTION_LIST_LIMIT,
                page: 0,
            }).toString();
            const response = await fetch(new URL('api/collections?' + params, process.env.APP_URL));
            const data = await response.json();
            setCollectionList(data.collections ?? []);
        } catch (e) {
            console.error(e);
        }
    }, []);
    const fetchBestSellersList = useCallback(async () => {
        try {
            const params = new URLSearchParams({
                limit: PRODUCT_LIST_LIMIT,
                page: 0,
            }).toString();
            const response = await fetch(new URL('api/products/bestSellers?' + params, process.env.APP_URL));
            const data = await response.json();
            setBestSellersList(data.products ?? []);
        } catch (e) {
            console.error(e);
        }
    }, []);
    const fetchProductList = useCallback(async () => {
        try {
            const params = new URLSearchParams({
                limit: PRODUCT_LIST_LIMIT,
                page: 0,
                v: 'October Mountain Products',
                q: 'Bow',
            }).toString();
            const response = await fetch(new URL('api/products/filter?' + params, process.env.APP_URL));
            const data = await response.json();
            setProductList(data.products ?? []);
        } catch (e) {
            console.error(e);
        }
    }, []);
    const fetchArticleList = useCallback(async () => {
        try {
            const params = new URLSearchParams({
                limit: ARTICLE_LIST_LIMIT,
                page: 0,
            }).toString();
            const response = await fetch(new URL('api/articles?' + params, process.env.APP_URL));
            const data = await response.json();
            setArticleList(data.articles ?? []);
        } catch (e) {
            console.error(e);
        }
    }, []);
    useEffect(() => {
        fetchCollectionList();
        fetchBestSellersList();
        fetchProductList();
        fetchArticleList();
    }, [fetchCollectionList, fetchBestSellersList, fetchProductList, fetchArticleList]);
    return (<>
        <SEO
            seoTitle={appConfig.seoTitle ?? appContext.config.storeName}
            seoType={'website'}
            seoDescription={appConfig.seoDescription}
            canonicalUrl={process.env.APP_URL}
        />
        <HeroSlideshow
            isMobile={isMobile}
            items={slideShowItems}></HeroSlideshow>
        <FeatureList
            isMobile={isMobile}
            features={featureList} />
        {collectionList.length > 0 && (<CollectionList
            collections={collectionList} />)}
        {productList.length > 0 && (<ProductList
            isMobile={isMobile}
            featured={true}
            products={bestSellersList} />)}
        <div
            style={{ textAlign: 'center' }}>
            <OverlayTrigger
                show={showQuoteForm}
                overlay={<QuoteForm
                    handleToggle={(toggle) => setShowQuoteForm(toggle)} />
                }>
                <button
                    className={'home-btn'}
                    onClick={() => setShowQuoteForm(true)}>
                    Request a Quote
                </button>
            </OverlayTrigger>
        </div>
        {productList.length > 0 && (<>
            <h2
                className={'home-heading'}><strong>Get better. Faster!</strong></h2>
            <ProductList
                isMobile={isMobile}
                products={productList.slice(0, 10)}
                featured={false}
                minimized={true} />
        </>)}
        <div
            style={{ textAlign: 'center' }}>
            <Link
                className={'home-btn'}
                to={appConfig.featuredCollection.url}>{appConfig.featuredCollection.buttonText}</Link>
        </div>
        {articleList.length > 0 && (<>
            <h2
                className={'home-heading'}><strong>Blog Posts</strong></h2>
            <ArticleList
                isMobile={isMobile}
                articles={articleList} />
            <Link
                className={'article-list-item--read-more'}
                to={articlePath[0].pathBeforeParams}>See more <BiChevronRight
                    size={'15px'} /></Link>
        </>)}
        <SiteReviews
            isMobile={isMobile} />
    </>);
};

Homepage.propTypes = {
    isMobile: bool,
};

export default Homepage;